import { Directive, HostListener, OnDestroy, OnInit, SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { Observable, Subscription } from 'rxjs';
import { isNil } from 'lodash';
import { HttpClient } from '@angular/common/http';
import { AuthService } from '@app/shared/services/auth.service';
import { EventsService } from '@app/shared/services/events.service';
import { MunicipalityService } from '@app/shared/services/api/municipality.service';
import { OverlayLoaderService } from '@app/shared/services/overlay-loader.service';
import { UserMunicipalitiesService } from '@app/shared/services/user-municipalities.service';
import { MicrofrontendUtils } from '@app/shared/mfe.utils';
import { MapStoreMunicipality } from '@app/shared/models/municipality/Municipality';
import { getTenantFullName } from '@app/shared/constants';
import { environment } from '@env/environment';
import { PermissionCode } from '@app/shared/enums';

@Directive()
export abstract class MfeBaseComponent implements OnInit, OnDestroy {
  protected mfeSubscription$ = new Subscription();

  protected abstract readonly mfeUrl: URL;
  protected abstract readonly element: string;
  protected abstract readonly parentElement: string;
  protected abstract readonly routePath: string;
  protected abstract readonly navigateEventName: string;
  protected abstract readonly pagePermission: PermissionCode;

  protected abstract getAdditionalEventPayload(municipality: MapStoreMunicipality): Record<string, any>;

  protected changedEventName = 'municipalityChangedEvent';

  get environment() {
    return environment;
  }

  constructor(
    public sanitizer: DomSanitizer,
    public httpClient: HttpClient,
    public overlayLoaderService: OverlayLoaderService,
    public municipalityService: MunicipalityService,
    protected readonly userMunicipalitiesService: UserMunicipalitiesService,
    protected readonly authService: AuthService,
    protected readonly eventsService: EventsService,
    protected readonly router: Router
  ) {}

  public ngOnInit(): void {
    let isLoaded = false;
    this.overlayLoaderService.isLoading = true;
    this.mfeSubscription$ = this.authService.permissionLoadComplete$
      .pipe(
        withLatestFrom(this.userMunicipalitiesService.currentMunicipality$),
        filter(([permissionLoaded, currentMunicipality]) => !isNil(currentMunicipality) && permissionLoaded),
        switchMap(([_, currentMunicipality]) => {
          if (!this.authService.permissions[this.pagePermission]) {
            this.navigateToHome();
            return [];
          }
          return [currentMunicipality];
        }),
        map((currentMunicipality) => this.getMicroFrontEndData(isLoaded, currentMunicipality)),
        switchMap((data) => this.loadMfe(data))
      )
      .subscribe({
        next: () => (isLoaded = true),
        error: () => (this.overlayLoaderService.isLoading = false),
      });

    this.mfeSubscription$.add(
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationEnd) {
          if (!event.urlAfterRedirects.includes(this.routePath)) {
            return;
          }
          this.notifyRemote(event.urlAfterRedirects);
        }
      })
    );
  }

  public ngOnDestroy(): void {
    MicrofrontendUtils.unload({
      path: MicrofrontendUtils.getUrl(this.mfeUrl),
      element: this.element,
      parentElement: this.parentElement,
    });
    this.mfeSubscription$.unsubscribe();
    this.overlayLoaderService.setLoadingToFalse();
  }

  private getMicroFrontEndData(isLoaded: boolean, currentMunicipality: MapStoreMunicipality) {
    const path =
      this.sanitizer.sanitize(SecurityContext.RESOURCE_URL, this.sanitizer.bypassSecurityTrustResourceUrl(MicrofrontendUtils.getUrl(this.mfeUrl))) ?? '';

    return {
      microfrontend: {
        path,
        element: this.element,
        parentElement: this.parentElement,
        loaded: isLoaded,
      },
      municipality: currentMunicipality,
      tenant: environment.tenant,
      tenantName: getTenantFullName(environment.tenant),
    };
  }

  private loadMfe(data: ReturnType<MfeBaseComponent['getMicroFrontEndData']>): Observable<any> {
    return MicrofrontendUtils.load(data.microfrontend).pipe(
      tap(() => {
        this.eventsService.publishEvent(this.changedEventName, {
          baseUrl: MicrofrontendUtils.getUrl(new URL(`${this.mfeUrl.origin}${this.mfeUrl.pathname.split('/').slice(0, -1).join('/')}`)),
          municipalityKey: data.municipality.key,
          municipalityName: data.municipality.name,
          municipalityGuid: data.municipality.guid,
          tenant: environment.tenant,
          tenantName: getTenantFullName(environment.tenant),
          user: this.authService.currentUser,
          proxyApiUrl: environment.proxyApiURL,
          proxyDirectUrl: environment.proxyDirectUrl,
          ...this.getAdditionalEventPayload(data.municipality),
        });
        this.overlayLoaderService.isLoading = false;
        this.overlayLoaderService.ignoreChanges = true;
      })
    );
  }

  private notifyRemote(url: string): void {
    this.eventsService.publishEvent(this.navigateEventName, { url });
  }

  @HostListener('window:navigateToHelpAndContact', ['$event'])
  navigateToHelpContact(event?: Event): void {
    const customEvent = event as CustomEvent<{ target?: '_blank' }> | undefined;

    if (customEvent?.detail?.target === '_blank') {
      const url = this.router.serializeUrl(this.router.createUrlTree(['/kontakt']));
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }

    this.router.navigate(['/kontakt'], { replaceUrl: true });
  }

  @HostListener('window:navigateToHome', ['$event'])
  navigateToHome(_event?: Event): void {
    this.router.navigate(['/instrumententafel'], { replaceUrl: true });
  }

  @HostListener('window:popstate', ['$event'])
  onPopState(_event: PopStateEvent): void {
    this.notifyRemote(globalThis.location.pathname);
  }
}
